import cx from 'classnames';
import type { FunctionComponent, MouseEventHandler } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';

import { useBoardSwitcherWorkspaceFragment } from './BoardSwitcherWorkspaceFragment.generated';
import { getBoardSwitcherSource } from './getBoardSwitcherSource';

import * as styles from './WorkspaceTabButton.module.less';

interface WorkspaceTabButtonProps {
  workspaceId: string | null;
  isSelected?: boolean;
}

export const WorkspaceTabButton: FunctionComponent<WorkspaceTabButtonProps> = ({
  workspaceId,
  isSelected = false,
}) => {
  const intl = useIntl();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data } = useBoardSwitcherWorkspaceFragment({
    from: { id: workspaceId ?? null },
    returnPartialData: true,
  });

  const onClick = useCallback<MouseEventHandler>(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'workspaceTabButton',
      source: getBoardSwitcherSource(),
      attributes: {
        workspaceFilter: workspaceId ?? 'all',
      },
    });
    boardSwitcherSharedState.setValue({
      selectedWorkspaceId: workspaceId,
    });
  }, [workspaceId]);

  const scrollTabIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      buttonRef.current?.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
      });
    });
  }, []);

  useEffect(() => {
    if (isSelected) {
      scrollTabIntoView();
    }
  }, [isSelected, scrollTabIntoView]);

  let buttonText: string = intl.formatMessage({
    id: 'templates.board_switcher.all',
    defaultMessage: 'All',
    description: 'Text on the all workspaces tab',
  });

  if (workspaceId) {
    buttonText =
      data?.displayName ??
      data?.name ??
      intl.formatMessage({
        id: 'templates.board_switcher.unknown-workspace',
        defaultMessage: 'Unknown workspace',
        description: 'Title of an unknown workspace on the board switcher',
      });
  }

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      title={buttonText}
      role="tab"
      id={`workspace-tab-${workspaceId}`}
      aria-controls={`workspace-tabpanel-${workspaceId}`}
      aria-selected={isSelected}
      className={cx({
        [styles.button]: true,
        [styles.selected]: isSelected,
      })}
    >
      <span>{buttonText}</span>
    </button>
  );
};
